/**
 * Pure helpers for the manual editor. The editor's state IS the JSON document (what the Advanced
 * tab shows and what `PUT` sends): every form control reads a value at a path and writes a new
 * document back with `setIn`/`setOrDelete`, so a document nobody touched round-trips unchanged
 * (unknown keys included) and the form and the JSON can never disagree.
 */

export type Obj = Record<string, unknown>
export type PathPart = string | number

export const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v)

export function getIn(root: unknown, path: readonly PathPart[]): unknown {
  let cur: unknown = root
  for (const p of path) {
    if (Array.isArray(cur) && typeof p === 'number') cur = cur[p]
    else if (isObj(cur)) cur = cur[String(p)]
    else return undefined
  }
  return cur
}

/** A copy of `root` with `value` at `path` (missing containers are made: `[]` before a number). */
export function setIn<T>(root: T, path: readonly PathPart[], value: unknown): T {
  if (path.length === 0) return value as T
  const [head, ...rest] = path as [PathPart, ...PathPart[]]
  if (typeof head === 'number') {
    const arr = Array.isArray(root) ? [...(root as unknown[])] : []
    arr[head] = setIn(arr[head], rest, value)
    return arr as T
  }
  const obj: Obj = isObj(root) ? { ...root } : {}
  obj[head] = setIn(obj[head], rest, value)
  return obj as T
}

/** A copy of `root` without the key (or array item) at `path`. */
export function deleteIn<T>(root: T, path: readonly PathPart[]): T {
  if (path.length === 0) return root
  const parent = getIn(root, path.slice(0, -1))
  const last = path[path.length - 1]!
  if (Array.isArray(parent) && typeof last === 'number') {
    if (last < 0 || last >= parent.length) return root
    return setIn(root, path.slice(0, -1), parent.filter((_, i) => i !== last))
  }
  if (!isObj(parent) || !(String(last) in parent)) return root
  const next = { ...parent }
  delete next[String(last)]
  return path.length === 1 ? (next as T) : setIn(root, path.slice(0, -1), next)
}

/** Nothing worth keeping: `undefined`, `null`, `''`, `[]`, `{}` (a `false` IS kept). */
export const isEmpty = (v: unknown): boolean => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || (isObj(v) && Object.keys(v).length === 0)

/** Set `value`, or drop the key when it is empty (so optional fields never become `""`). */
export function setOrDelete<T>(root: T, path: readonly PathPart[], value: unknown): T {
  return isEmpty(value) ? deleteIn(root, path) : setIn(root, path, value)
}

/* -------------------------------------------------------------------------- */
/* Lists                                                                       */
/* -------------------------------------------------------------------------- */

export function moveItem<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by
  if (to < 0 || to >= list.length) return [...list]
  const out = [...list]
  const [item] = out.splice(index, 1)
  out.splice(to, 0, item as T)
  return out
}

export function removeAt<T>(list: readonly T[], index: number): T[] {
  return list.filter((_, i) => i !== index)
}

export function insertAt<T>(list: readonly T[], index: number, item: T): T[] {
  const out = [...list]
  out.splice(index, 0, item)
  return out
}

export const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T))

export const asList = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
export const asObj = (v: unknown): Obj => (isObj(v) ? v : {})
export const asStrings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [])

/* -------------------------------------------------------------------------- */
/* Names + ids                                                                 */
/* -------------------------------------------------------------------------- */

/** `^[a-z][a-z0-9_]{1,40}$` (the registry's ids). */
export const ID_RE = /^[a-z][a-z0-9_]{1,40}$/
/** `^[a-z][a-z0-9_]{1,24}$` (role names). */
export const ROLE_RE = /^[a-z][a-z0-9_]{1,24}$/

/** "Wave twice!" → `wave_twice`; `''` when nothing usable is left. */
export function slugify(text: string, max = 40): string {
  let s = text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  if (s && !/^[a-z]/.test(s)) s = `a_${s}`
  s = s.slice(0, max + 1).replace(/_+$/, '')
  return s.length >= 2 ? s : ''
}

/** `base`, else `base_2`, `base_3`… not in `taken`. */
export function uniqueName(base: string, taken: Iterable<string>, max = 40): string {
  const used = new Set(taken)
  const stem = base.slice(0, max - 3) || 'new'
  if (!used.has(stem)) return stem
  for (let n = 2; n < 1000; n++) if (!used.has(`${stem}_${n}`)) return `${stem}_${n}`
  return `${stem}_${Date.now().toString(36)}`
}

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

/** Keys whose value is an event ref (`role:event`). */
const EVENT_KEYS = new Set(['at_event', 'with', 'on'])
/** Text the parent wrote: never a ref. */
const TEXT_KEYS = new Set(['label', 'description', 'notes', 'id'])
/** Objects keyed by role name. */
const ROLE_KEYED = new Set(['tracks', 'clips'])

function renameKey(o: Obj, from: string, to: string): Obj {
  const out: Obj = {}
  for (const [k, v] of Object.entries(o)) out[k === from ? to : k] = v
  return out
}

function rewriteRefs(value: unknown, from: string, to: string, key: string | null, parent: Obj | null): unknown {
  if (typeof value === 'string') {
    if (key && TEXT_KEYS.has(key)) return value
    if (value === `$${from}` || value.startsWith(`$${from}.`)) return `$${to}${value.slice(from.length + 1)}`
    if (key && EVENT_KEYS.has(key) && value.startsWith(`${from}:`)) return `${to}:${value.slice(from.length + 1)}`
    if (key === 'role' && parent?.k === 'role' && value === from) return to
    if (key === 'idea_template') return value.split(`{${from}}`).join(`{${to}}`)
    return value
  }
  if (Array.isArray(value)) return value.map((v) => rewriteRefs(v, from, to, null, null))
  if (isObj(value)) {
    const src = key && ROLE_KEYED.has(key) ? renameKey(value, from, to) : value
    const out: Obj = {}
    for (const [k, v] of Object.entries(src)) out[k] = rewriteRefs(v, from, to, k, src)
    return out
  }
  return value
}

/**
 * Rename a participant everywhere: its `roles` key (order kept), every step's track, its clips,
 * `{name}` in the idea, and every ref to it (`$name`, `$name.socket.hand`, `name:release`,
 * a role clause's `role`). A fallback's `map` keys are the OTHER action's roles and stay.
 */
export function renameRole<T extends Obj>(doc: T, from: string, to: string): T {
  if (from === to || !isObj(doc.roles) || !(from in doc.roles) || to in doc.roles) return doc
  const out: Obj = {}
  for (const [k, v] of Object.entries(doc)) {
    // Roles are keyed by name; their `state` may point at each other (`holder: $thrower`).
    if (k === 'roles') out.roles = rewriteRefs(renameKey(v as Obj, from, to), from, to, null, null)
    else out[k] = rewriteRefs(v, from, to, k, doc)
  }
  return out as T
}

/** Remove a participant and what only it did (its tracks and clips); other refs are left for the checker. */
export function removeRole<T extends Obj>(doc: T, name: string): T {
  let out = deleteIn(doc, ['roles', name])
  out = deleteIn(out, ['clips', name])
  const program = asList(out.program)
  if (program.length) out = setIn(out, ['program'], program.map((s) => (isObj(s) && isObj(s.tracks) ? deleteIn(s, ['tracks', name]) : s)))
  return out
}

/** Role names suggested for a new participant of a type. */
const ROLE_BASE: Record<string, string> = { character: 'friend', object: 'thing', scenery: 'scenery', feature: 'place', spot: 'spot' }

export function newRoleName(type: string, taken: Iterable<string>): string {
  return uniqueName(ROLE_BASE[type] ?? 'helper', taken, 25)
}

/* -------------------------------------------------------------------------- */
/* Blanks                                                                      */
/* -------------------------------------------------------------------------- */

/** A new action: one character who does one move. Saving needs only a name. */
export function blankAction(): Obj {
  return {
    id: '',
    label: '',
    description: '',
    category: 'play',
    idea_template: '{actor} feels happy',
    enabled: true,
    roles: { actor: { types: ['character'], required: true } },
    preconditions: [],
    program: [{ phase: 'action', dur: 'clip', tracks: { actor: { clip: 'happy' } } }],
    result: [],
    duration: { min: 1, default: 2, max: 4 },
  }
}

/** A new world rule: when a thing lands in a place, the place splashes. */
export function blankRule(): Obj {
  return {
    id: '',
    label: '',
    description: '',
    enabled: true,
    priority: 50,
    once_per_pair: true,
    when: { event: 'land', subject: { types: ['object'] }, other: { types: ['feature'] } },
    then: [{ phase: 'react', dur: 'auto', tracks: { other: { react: { fx: 'splash' } } } }],
    result: [],
  }
}

/** A new step in which `role` plays a move. */
export function blankStep(role: string | undefined): Obj {
  return { phase: 'action', dur: 'clip', tracks: role ? { [role]: { clip: 'happy' } } : {} }
}

/** A fresh clause of `kind`, pointing at the first roles. */
export function blankClause(kind: string, roles: readonly string[]): Obj {
  const a = roles[0] ?? 'actor'
  const b = roles[1] ?? a
  switch (kind) {
    case 'role':
      return { k: 'role', role: a, types: ['character'] }
    case 'relation':
      return { k: 'relation', a: `$${a}`, rel: 'near', b: `$${b}` }
    case 'stage':
      return { k: 'stage', has: ['ground'] }
    case 'state':
      return { k: 'state', ref: `$${a}.mood`, eq: 'happy' }
    case 'any':
      return { k: 'any', any: [{ k: 'relation', a: `$${a}`, rel: 'near', b: `$${b}` }] }
    case 'not':
      return { k: 'not', not: { k: 'relation', a: `$${a}`, rel: 'far', b: `$${b}` } }
    default:
      return { k: kind }
  }
}

/* -------------------------------------------------------------------------- */
/* Values                                                                      */
/* -------------------------------------------------------------------------- */

/** What a parent typed as a state value: `true`/`false`, a number, `null`/`none`, else the text. */
export function parseValue(text: string): unknown {
  const t = text.trim()
  if (t === 'true') return true
  if (t === 'false') return false
  if (t === 'null') return null
  if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) return Number(t)
  return t
}

export function formatValue(v: unknown): string {
  if (v === undefined) return ''
  if (v === null) return 'null'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

/** A `$role.key` state ref split into its parts (`['', '']` when it is not one). */
export function splitStateRef(ref: unknown): [string, string] {
  if (typeof ref !== 'string' || !ref.startsWith('$')) return ['', '']
  const s = ref.slice(1)
  const i = s.indexOf('.')
  return i < 0 ? [s, ''] : [s.slice(0, i), s.slice(i + 1)]
}

/** A `role:event` ref split. */
export function splitEventRef(ref: unknown): [string, string] {
  if (typeof ref !== 'string') return ['', '']
  const i = ref.indexOf(':')
  return i < 0 ? ['', ref] : [ref.slice(0, i), ref.slice(i + 1)]
}

/** The document as JSON text (what the Advanced tab shows). */
export const pretty = (doc: unknown): string => JSON.stringify(doc, null, 2)
